/**
 * Supporting JS for Intelligems AB tests.
 * Group resolution lives in c-intelligems-tests.js; this file reacts to it.
 */

/* ======================================================================== *
 * Product card image carousel — every product card
 *
 * Was V_PV_MIX_04 (Intelligems d58e0415-3a50-4b98-99df-ccc55f03f662), concluded
 * on Var A: +7.7% revenue per visitor and +7.6% conversion rate over 15 days.
 * Every visitor now gets the strip, so there is no group to check and no class
 * to wait for — the "Render the carousel" checkbox under Theme settings ->
 * "Product Card Carousel" is the whole switch, and it decides whether
 * card-product.liquid renders the markup at all.
 *
 * The strip that snippets/c-mix04-card-carousel.liquid lays over a product
 * card's image is a native scroll-snap scroller, so the swipe, its momentum and
 * the snapping all belong to the browser. Nothing here drives the gesture — the
 * work is kept off the gesture path on purpose, because that is what makes a
 * carousel of this kind feel heavy:
 *
 *   - the position bars are repainted once per animation frame, not once per
 *     scroll event, so a flick does not thrash layout
 *   - the lazy frames are promoted the moment the card comes into view, so a
 *     swipe is never waiting on a request or a decode
 *   - the loop is closed only after the scroll has genuinely ended and the
 *     finger has left the screen, never mid-gesture
 *
 * The loop itself: the strip repeats frame 1 after the last frame, and the last
 * frame before frame 1. A swipe off either end lands on the repeat, and the
 * scroller is then handed to the matching real frame — same image, same pixels,
 * so the wrap cannot be seen. The leading repeat ships hidden, because a slide
 * in front of frame 1 would move the strip's start away from scroll offset 0 and
 * the correction would flash the wrong image over the cards above the fold. It
 * is revealed and compensated for in a single frame when the card comes into
 * view, with the visible frame unchanged.
 *
 * Its own IIFE on purpose: the block above returns early when the Price Ladder
 * test is not running, and this test must not be tied to that.
 *
 * Every listener is delegated from document, so cards swapped in by filtering,
 * sorting, pagination or a section render need no re-initialisation. A card the
 * snippet skipped — one image, a video in front, a horizontal card — has no
 * strip and so no layout box, which is why nothing here has to ask whether a
 * card has one: it never comes into view, never primes and is never the target
 * of a pointer.
 * ======================================================================== */
(function () {
  'use strict';

  const TRACK_CLASS = 'c-mix04__track';
  const TRACK = '.c-mix04__track';
  // Only a fallback for browsers without scrollend. Long enough to outlast the
  // gap between momentum frames, short enough not to be noticed.
  const SETTLE_MS = 140;
  const SUPPORTS_SCROLLEND = 'onscrollend' in window;
  // Frames to keep asking whether the strip has landed before giving up. Roughly
  // a second and a half, far longer than any snap animation.
  const SETTLE_FRAMES = 90;
  // The arrows' scroll is the only one that should animate, so it asks per call
  // rather than the track carrying scroll-behavior for every scroll it receives.
  // Read live, because the setting can be changed while the page is open.
  const reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function stepBehavior() {
    return reduceMotion && reduceMotion.matches ? 'auto' : 'smooth';
  }

  const painting = new WeakSet();
  // Per-track state kept off the DOM on purpose. Splide clones slides with
  // cloneNode for its infinite rows, which copies attributes but not scroll
  // offsets — a data attribute would travel to the clone and tell us a strip was
  // already primed and open when it is in fact sitting on the wrong frame at
  // offset 0. A clone is a new node, so it is in none of these.
  const primed = new WeakSet();
  const opened = new WeakSet();
  const watched = new WeakSet();
  const settling = new WeakMap();
  // Pending "has it arrived yet?" frame, per track, and how many of them a track
  // has spent waiting — the bound on that poll.
  const confirming = new WeakMap();
  const waiting = new WeakMap();
  // Where an arrow has sent a track, as a slide index. Read instead of the live
  // scroll offset when the next arrow click arrives, because a smooth scroll that
  // is still running reports a position half way between two slides — round that
  // and a second click either lands back where it started or skips a frame. This
  // is what made the arrows feel unreliable.
  const targets = new WeakMap();
  // The track a finger is currently on, if any. Deliberately a single reference
  // rather than a set: a release that lands outside the card it started on must
  // still clear the hold, or that card would never settle again.
  let held = null;

  function trackFrom(node) {
    return node && node.closest ? node.closest(TRACK) : null;
  }

  function isTrack(node) {
    return !!node && !!node.classList && node.classList.contains(TRACK_CLASS);
  }

  // Real frames, i.e. not counting either of the loop's repeats.
  function realCount(track) {
    return parseInt(track.dataset.count, 10) || 1;
  }

  function leadSlide(track) {
    return track.querySelector('.c-mix04__slide--lead');
  }

  // How many slides sit in front of real frame 1 — 1 once the leading repeat is
  // in play, 0 before that. Every offset below is expressed against it, so the
  // same arithmetic holds either way.
  function leadOffset(track) {
    const lead = leadSlide(track);
    return lead && !lead.hidden ? 1 : 0;
  }

  // The pitch between frames, and every offset in this block is a multiple of
  // it. Measured with getBoundingClientRect rather than clientWidth: clientWidth
  // is rounded to a whole pixel, and a card in a fluid grid is very often a
  // fractional number of pixels wide. That error is multiplied by the frame
  // index, so by the end of a strip it is easily more than a pixel — enough to
  // put the "is it on a frame boundary?" test on the wrong side, and enough for a
  // correction derived from it to knock the strip off a boundary it was already
  // sitting on. Slides are flex: 0 0 100%, so this is their width too.
  function frameWidth(track) {
    return track.getBoundingClientRect().width || track.clientWidth;
  }

  function slideAt(track) {
    const width = frameWidth(track);
    return width ? Math.round(track.scrollLeft / width) : 0;
  }

  function frameIndex(track) {
    const count = realCount(track);
    return (((slideAt(track) - leadOffset(track)) % count) + count) % count;
  }

  function paintBars(track, forced) {
    const root = track.parentElement;
    if (!root) return;

    const index = forced == null ? frameIndex(track) : forced;
    root.querySelectorAll('.c-mix04__bar').forEach(function (bar, position) {
      bar.classList.toggle('c-mix04__bar--active', position === index);
    });
  }

  // Once per animation frame at most. A flick fires scroll events far faster
  // than the screen refreshes, and each repaint reads layout.
  function schedulePaint(track) {
    if (painting.has(track)) return;
    painting.add(track);
    window.requestAnimationFrame(function () {
      painting.delete(track);
      paintBars(track);
    });
  }

  // The exchange of a repeat for the real frame showing the same image. Instant,
  // and it can only be instant because the track carries no scroll-behavior of
  // its own — see the note on .c-mix04__track in c-custom.css. It used to toggle
  // a class to suppress a CSS `scroll-behavior: smooth` for the duration, which
  // is what left the strip stranded between two frames: the class had to win a
  // style flush before the assignment, and when it did not the wrap animated
  // across the whole strip against the snap animation still running underneath.
  //
  // Snapping is switched off across the assignment, and that is the part that
  // makes the wrap hold. A mandatory snap container keeps its own record of which
  // slide is the current snap target, and a programmatic scroll does not reliably
  // move that record — iOS Safari in particular leaves it pointing at the slide
  // the strip was on before the jump. The next swipe is then resolved against a
  // target the strip is no longer sitting on, so it snaps straight back: a wrap
  // that lands on the right frame, followed by a strip that will not advance at
  // all. Clearing scroll-snap-type discards the record; restoring it makes the
  // browser pick a target from where the strip actually is now.
  function jumpTo(track, left) {
    const snap = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';
    track.scrollLeft = left;
    // Forces the offset to be applied while snapping is still off, so the two
    // style changes cannot be coalesced into one pass with snapping never
    // actually having been released.
    void track.offsetWidth;
    track.style.scrollSnapType = snap;
  }

  // At rest on one of the repeats: hand the scroller to the real frame showing
  // the same image. Nothing moves on screen.
  function closeLoop(track) {
    const width = frameWidth(track);
    if (!width) return;

    const count = realCount(track);
    const lead = leadOffset(track);
    const at = slideAt(track);

    if (at < lead) {
      jumpTo(track, (lead + count - 1) * width);
    } else if (at > lead + count - 1) {
      jumpTo(track, lead * width);
    }
  }

  // Within a pixel of a frame boundary, i.e. the snap has finished rather than
  // the scroller having merely stopped being reported.
  function onSnapPoint(track) {
    const width = frameWidth(track);
    if (!width) return false;
    const past = track.scrollLeft % width;
    return past < 1 || width - past < 1;
  }

  function settle(track) {
    // Never mid-gesture: a teleport under a finger that is still down fights the
    // touch scroll, and that is what a broken loop looks like.
    if (held === track) return;

    const width = frameWidth(track);
    if (!width) return;

    // Arrival is judged by position, not by timing. Momentum and the snap
    // animation both outlive the events that report them, and scrollend does not
    // fire at the same moment in every browser — but a strip that is still
    // travelling is by definition between two frames, and one that has arrived is
    // on a frame boundary. So the only safe moment to touch the offset is when it
    // is already on one, and until then the answer is to wait.
    //
    // What this replaced: a check for the offset having stopped changing across
    // two samples, and a correction that moved a stopped-but-off-boundary strip
    // to the nearest frame. Both were wrong. Scroll offsets are updated off the
    // main thread, so two consecutive samples routinely read the same value while
    // the animation is still running; the strip was then declared arrived at, say,
    // 242 of a 177-wide frame, and "nearest" rounded that back down to 177 — the
    // frame it had just left. The swipe was undone a fraction of a second after
    // it finished, which is exactly the frame that would not advance. Mandatory
    // snapping guarantees the strip lands on a boundary on its own, so there was
    // never anything to correct.
    if (!onSnapPoint(track)) {
      confirmSettle(track);
      return;
    }

    waiting.delete(track);
    closeLoop(track);
    targets.set(track, slideAt(track));
    paintBars(track);
    // Safe to grow the strip now: it is at rest, so compensating the offset
    // cannot collide with a scroll in flight.
    openLoop(track);
  }

  function scheduleSettle(track) {
    window.clearTimeout(settling.get(track));
    settling.set(
      track,
      window.setTimeout(function () {
        settle(track);
      }, SETTLE_MS)
    );
  }

  // The "has it arrived yet?" re-check, on the next frame rather than after
  // another SETTLE_MS. How long the wrap takes is not cosmetic: until it has run
  // the strip is parked on a repeat, and the repeat that ends a cycle is the last
  // slide in the scroller — a swipe forward from it has nothing to its right, so
  // it rubber-bands and the frame does not change. Every millisecond spent on the
  // repeat is a millisecond in which a swipe does nothing, so the confirmation is
  // a frame, not a timeout.
  //
  // Bounded, because it is the only thing standing between "the strip never lands
  // on a boundary" and a poll that runs for the life of the page. Reaching the
  // bound is not a failure worth correcting — the next gesture settles it — so it
  // simply stops.
  function confirmSettle(track) {
    const attempts = (waiting.get(track) || 0) + 1;
    if (attempts > SETTLE_FRAMES) {
      waiting.delete(track);
      return;
    }
    waiting.set(track, attempts);
    window.cancelAnimationFrame(confirming.get(track));
    confirming.set(
      track,
      window.requestAnimationFrame(function () {
        settle(track);
      })
    );
  }

  // Promotes the lazy frames, so a swipe is never waiting on a request or a
  // decode. Touches nothing about the scroll, so it is safe at any moment,
  // including on the first touch of a gesture.
  function prime(track) {
    if (primed.has(track)) return;
    primed.add(track);
    track.querySelectorAll('img[loading="lazy"]').forEach(function (image) {
      image.loading = 'eager';
    });
  }

  // Reveals the loop's leading repeat and adds the slide it inserted to the
  // scroll offset, so the frame on screen does not move.
  //
  // This does scroll, so it is only ever called when the strip is at rest — from
  // the observer as the card comes into view, or from settle once a gesture has
  // finished. Called during a gesture it moved the scroller under the finger,
  // cancelling the snap and stalling the strip between two frames, which is why
  // pointerdown now only primes.
  function openLoop(track) {
    if (opened.has(track)) return;

    const lead = leadSlide(track);
    const width = frameWidth(track);
    // No layout yet — the card is not on screen, so there is nothing to correct
    // and nothing to mark done. It will be brought in when it is.
    if (!lead || !width) return;

    opened.add(track);

    // A card that arrived as a Splide clone of one whose loop was already open.
    // cloneNode copies the revealed leading repeat but not the scroll offset that
    // pays for it, so the clone starts at offset 0 with a slide in front of frame
    // 1 — showing the last frame where frame 1 belongs. Put it back to the state
    // the snippet ships and open it below like any other card. Why the flags here
    // are WeakSets and not data attributes: an attribute is cloned, so a clone
    // would claim to be primed and open while sitting on the wrong frame.
    if (!lead.hidden) {
      lead.hidden = true;
      void track.offsetWidth;
    }

    const at = slideAt(track);
    lead.hidden = false;
    void track.offsetWidth;
    jumpTo(track, (at + 1) * width);
    // Every slide index shifts by one the moment the leading repeat appears, so
    // anything already recorded against the old numbering has to move with it.
    targets.set(track, at + 1);
  }

  function activate(track) {
    prime(track);
    openLoop(track);
  }

  const watcher =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              watcher.unobserve(entry.target);
              activate(entry.target);
            });
          },
          // Brought in before it is reached, so the frames are decoded by the
          // time the card is under a thumb.
          { rootMargin: '300px 0px' }
        )
      : null;

  function watchAll() {
    if (!watcher) return;
    document.querySelectorAll(TRACK).forEach(function (track) {
      if (watched.has(track)) return;
      watched.add(track);
      watcher.observe(track);
    });
  }

  document.addEventListener(
    'scroll',
    function (event) {
      if (!isTrack(event.target)) return;
      schedulePaint(event.target);
      if (!SUPPORTS_SCROLLEND) scheduleSettle(event.target);
    },
    true
  );

  if (SUPPORTS_SCROLLEND) {
    document.addEventListener(
      'scrollend',
      function (event) {
        if (isTrack(event.target)) settle(event.target);
      },
      true
    );
  }

  document.addEventListener(
    'pointerdown',
    function (event) {
      const track = trackFrom(event.target);
      if (!track) return;

      window.cancelAnimationFrame(confirming.get(track));
      window.clearTimeout(settling.get(track));

      // A hold whose release never arrived — a pointerup swallowed by a section
      // re-render, a card replaced under the finger by filtering — would leave
      // settle() returning early for that track for the rest of the page's life.
      // It would never wrap, so it would sit on a repeat refusing to advance
      // while every other card behaved. A new touch anywhere in a strip is proof
      // the old one is over, so the stranded track is handed back here.
      if (held && held !== track) {
        const stranded = held;
        held = null;
        scheduleSettle(stranded);
      }

      // Last chance to take a pending wrap, and the one that makes a cycle roll
      // into the next without a dead swipe.
      //
      // The gesture that ends a cycle leaves the strip on the trailing repeat,
      // and the wrap off it cannot run while that gesture is still in progress.
      // So there is always a window where the strip shows frame 1 but is sitting
      // on the last slide in the scroller — and a swipe forward from the last
      // slide has nothing to its right, so it rubber-bands and the frame does not
      // change. That is the swipe that felt like it was ignored.
      //
      // The finger is down but has not moved, so no scroll is under way and this
      // is safe. Taking the wrap now means the gesture about to start begins from
      // the real frame and advances normally. Guarded on being at rest, because a
      // finger put down to catch a strip still in motion is off a boundary and
      // must not be teleported.
      //
      // No repaint follows it: a repeat and the frame it is exchanged for are the
      // same image, so the bars already read correctly either side of the wrap,
      // and this is the gesture path — a layout read not taken is worth having.
      if (!held && onSnapPoint(track)) closeLoop(track);

      held = track;
      targets.delete(track);
      waiting.delete(track);
      // Belt and braces: a card inserted by filtering may not have been watched.
      // Priming only — revealing the leading repeat here would scroll the strip
      // under the finger that is starting the swipe. settle() opens the loop once
      // the gesture is over.
      prime(track);
    },
    { passive: true }
  );

  function release() {
    if (!held) return;
    const track = held;
    held = null;
    // Momentum outlives the release, so this only settles once it has run out.
    scheduleSettle(track);
  }

  document.addEventListener('pointerup', release, { passive: true });
  document.addEventListener('pointercancel', release, { passive: true });

  // One frame in either direction, from where the last arrow meant to go rather
  // than from wherever the scroller happens to be at this instant.
  function step(track, direction) {
    const width = frameWidth(track);
    if (!width) return;

    const count = realCount(track);
    const lead = leadOffset(track);
    const first = lead;
    const last = lead + count - 1;

    let from = targets.has(track) ? targets.get(track) : slideAt(track);

    // Sitting on — or heading for — one of the loop's repeats, because the wrap
    // has not run yet. Hand it to the real frame showing the same image before
    // stepping off it, so a fast second click advances instead of stalling.
    if (from < first || from > last) {
      from = from < first ? last : first;
      jumpTo(track, from * width);
    }

    const to = from + direction;

    // Backwards off frame 1 on a card whose leading repeat never came into play:
    // there is no slide to the left to animate through, so take the wrap outright.
    if (to < 0) {
      jumpTo(track, last * width);
      targets.set(track, last);
      paintBars(track, count - 1);
      return;
    }

    targets.set(track, to);
    // The bars answer the click immediately rather than at the half way point of
    // the scroll, which is what makes an arrow feel connected to the strip.
    paintBars(track, (((to - lead) % count) + count) % count);
    track.scrollTo({ left: to * width, behavior: stepBehavior() });
  }

  // Capture phase, so the arrow is handled before the click reaches the frame
  // anchor underneath it.
  document.addEventListener(
    'click',
    function (event) {
      const arrow = event.target.closest ? event.target.closest('.c-mix04__arrow') : null;
      if (!arrow) return;

      event.preventDefault();
      event.stopPropagation();

      const root = arrow.closest('.c-mix04');
      const track = root ? root.querySelector(TRACK) : null;
      if (!track) return;

      activate(track);
      step(track, arrow.dataset.direction === 'next' ? 1 : -1);
    },
    true
  );

  // Splide's drag, kept off a strip. The strip runs inside slider rows too, so on
  // a Splide row two things want the same horizontal swipe: the browser scrolling
  // the strip, and Splide's own pointer-driven drag moving the row. Unarbitrated
  // both happen at once and a swipe on a card slides the card and the row
  // together.
  //
  // Splide binds its drag start on .splide__track for 'touchstart mousedown' with
  // capture, so the only place to get in front of it is a capture listener higher
  // up the propagation path — document. A touch that starts inside a strip is
  // stopped here and never reaches it, so the swipe belongs to the strip; a touch
  // anywhere else on the row — the gaps between cards, the card's text, the
  // arrows, the dots — is untouched and still drags the row.
  //
  // touchstart only, deliberately. Mouse drag is left to Splide: a mouse cannot
  // scroll the strip by dragging it, and the strip is advanced by its own hover
  // arrows on desktop, so blocking mousedown would take row dragging away and
  // give nothing back. Native scrolling does not depend on the event reaching a
  // listener, so stopping it here costs the strip nothing.
  document.addEventListener(
    'touchstart',
    function (event) {
      if (trackFrom(event.target)) event.stopPropagation();
    },
    { capture: true, passive: true }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAll);
  } else {
    watchAll();
  }

  // Splide regenerates its clones when it crosses one of its own breakpoints —
  // a rotated phone, a resized window — so a row can gain tracks that have never
  // been observed. Coalesced onto the trailing edge of the resize, because
  // watchAll walks the document.
  let rewatching;
  window.addEventListener(
    'resize',
    function () {
      window.clearTimeout(rewatching);
      rewatching = window.setTimeout(watchAll, 200);
    },
    { passive: true }
  );

  // Kept past the test: Intelligems answers after the document is ready, and a
  // still-running test's onsite edits can bring in cards this walk has never
  // seen. shopify:section:load does not cover those.
  window.addEventListener('ig:ready', watchAll);
  document.addEventListener('shopify:section:load', watchAll);

  // Console helper: cMix04Debug() reports every strip on the page and the state
  // it is in, so a grid that is silently doing nothing can be identified in one
  // call.
  window.cMix04Debug = function () {
    const carousels = [];
    document.querySelectorAll(TRACK).forEach(function (track) {
      const first = track.querySelector('.c-mix04__slide:not(.c-mix04__slide--lead)');
      carousels.push({
        url: first ? first.getAttribute('href') : null,
        frames: realCount(track),
        frame: frameIndex(track),
        slide: slideAt(track),
        leadOffset: leadOffset(track),
        primed: primed.has(track),
        ready: opened.has(track),
        clone: !!(track.closest && track.closest('.splide__slide--clone')),
        frameWidth: frameWidth(track),
        // false on a strip stranded between two frames, which is the one state
        // that is hard to read off the screen.
        snapped: onSnapPoint(track)
      });
    });

    return { scrollend: SUPPORTS_SCROLLEND, carousels: carousels };
  };
})();

/* ======================================================================== *
 * Cart page refresh — keep the shopper's language and the subtotal
 *
 * When something is added to the cart from the cart page itself (the Shipping
 * Warranty gift being added on its own is the usual case), Shrine's main.js
 * re-renders the line items from a hard-coded '/cart?section_id=...'. That URL
 * has no locale prefix, so on /fr-fr, /de-at, /ja-jp etc. the list comes back
 * in English, and only <cart-items> is replaced, so the subtotal in the footer
 * still shows the total from before the add. main.js is hosted by Shrine and
 * cannot be edited here, so its CartItems.onCartUpdate is replaced: the same
 * sections are fetched through routes.cart_url, which carries the locale, and
 * the footer subtotal and the header cart count are refreshed with them.
 * ======================================================================== */
(function () {
  customElements.whenDefined('cart-items').then(function () {
    const CartItems = customElements.get('cart-items');
    const shrineOnCartUpdate = CartItems.prototype.onCartUpdate;
    if (typeof shrineOnCartUpdate !== 'function') return;

    CartItems.prototype.onCartUpdate = function () {
      const items = document.getElementById('main-cart-items');
      // cart-drawer-items inherits this method; leave anything other than the
      // cart page's own list to Shrine.
      if (this.tagName !== 'CART-ITEMS' || !items || !window.routes || !routes.cart_url) {
        return shrineOnCartUpdate.apply(this, arguments);
      }

      const footer = document.getElementById('main-cart-footer');
      const sections = [items.dataset.id, 'cart-icon-bubble'];
      if (footer) sections.push(footer.dataset.id);

      fetch(routes.cart_url + '?sections=' + sections.join(','))
        .then(function (response) {
          return response.json();
        })
        .then(
          function (rendered) {
            const parse = function (html) {
              return new DOMParser().parseFromString(html || '', 'text/html');
            };

            const freshItems = parse(rendered[items.dataset.id]).querySelector('cart-items');
            if (freshItems) this.innerHTML = freshItems.innerHTML;

            if (footer) {
              const freshFooter = parse(rendered[footer.dataset.id]).getElementById('main-cart-footer');
              if (freshFooter) {
                footer.classList.toggle('is-empty', freshFooter.classList.contains('is-empty'));
                const current = footer.querySelectorAll('.js-contents');
                freshFooter.querySelectorAll('.js-contents').forEach(function (fresh, index) {
                  if (current[index]) current[index].innerHTML = fresh.innerHTML;
                });
              }
            }

            const bubble = document.getElementById('cart-icon-bubble');
            const freshBubble = parse(rendered['cart-icon-bubble']).querySelector('.shopify-section');
            if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
          }.bind(this)
        )
        .catch(function (error) {
          console.error(error);
        });
    };
  });
})();
